<template>
  <div class="my-center">
    <div class="my-center__inner">
      <aside class="mc-sidebar">
        <div class="mc-sidebar__panel">
          <header class="mc-sidebar__profile">
            <UserAvatar :size="88" />
            <div class="mc-sidebar__name">{{ displayName }}</div>
            <div class="mc-sidebar__meta">{{ displayPhone }}</div>
          </header>

          <nav class="mc-nav" aria-label="个人中心菜单">
            <div
              v-for="group in menuList"
              :key="group.key"
              class="mc-nav__section"
            >
              <div class="mc-nav__section-title">{{ group.header }}</div>
              <div class="mc-nav__group">
                <button
                  v-for="item in group.children"
                  :key="item.key"
                  type="button"
                  class="mc-nav__item"
                  :class="{
                    'mc-nav__item--active':
                      !item.route && activeMenu === item.key,
                  }"
                  @click="handleMenu(item)"
                >
                  <span class="mc-nav__icon" aria-hidden="true">
                    <component :is="menuIconMap[item.key]" />
                  </span>
                  <span class="mc-nav__label">{{ item.title }}</span>
                  <span class="mc-nav__trail" aria-hidden="true">
                    <svg
                      v-if="item.route"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </nav>
        </div>
      </aside>

      <main class="mc-main">
        <header class="mc-main__head">
          <h1 class="mc-main__title page-title">{{ currentPageTitle }}</h1>
          <p class="mc-main__subtitle">{{ currentPageSubtitle }}</p>
        </header>
        <div class="mc-main__body">
          <Account v-if="activeMenu === '1-1'" />
          <Address v-if="activeMenu === '1-2'" embedded />
        </div>
      </main>
    </div>

    <ProfileEditDialogs ref="profileEditRef" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, provide, h } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import UserAvatar from "@/components/UserAvatar.vue";
import Account from "@/views/myCenter/components/accountIndex.vue";
import Address from "@/components/addressIndex.vue";
import ProfileEditDialogs from "@/components/ProfileEditDialogs.vue";
import menus from "./datas.js";

const router = useRouter();
const userStore = useUserStore();
const menuList = ref(menus);
const activeMenu = ref("1-1");
const profileEditRef = ref(null);

const openNicknameDialog = () => {
  profileEditRef.value?.openNicknameDialog();
};

const openPhoneDialog = () => {
  profileEditRef.value?.openPhoneDialog();
};

provide("profileEdit", {
  openNicknameDialog,
  openPhoneDialog,
});

const userInfo = computed(() => userStore.userInfo);

const displayName = computed(() => userInfo.value?.username || "未设置昵称");

const displayPhone = computed(() => {
  const phone = userInfo.value?.phone;
  if (!phone) return userInfo.value?.id ? `ID ${userInfo.value.id}` : "";
  return phone;
});

const pageMeta = {
  "1-1": {
    title: "账号管理",
    subtitle: "管理资料修改与账号安全",
  },
  "1-2": {
    title: "地址管理",
    subtitle: "管理收货地址与联系方式",
  },
};

const currentPageTitle = computed(
  () => pageMeta[activeMenu.value]?.title || "个人中心"
);

const currentPageSubtitle = computed(
  () => pageMeta[activeMenu.value]?.subtitle || ""
);

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "1.75",
};

const menuIconMap = {
  "1-1": () =>
    h("svg", iconProps, [
      h("circle", { cx: "12", cy: "8", r: "4" }),
      h("path", { d: "M5 20c0-4 3.5-6 7-6s7 2 7 6" }),
    ]),
  "1-2": () =>
    h("svg", iconProps, [
      h("path", {
        d: "M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z",
      }),
      h("circle", { cx: "12", cy: "10", r: "2.5" }),
    ]),
  "1-3": () =>
    h("svg", iconProps, [
      h("rect", { x: "3", y: "6", width: "18", height: "14", rx: "2" }),
      h("path", { d: "M3 10h18" }),
      h("path", { d: "M7 15h4" }),
    ]),
};

const handleMenu = (item) => {
  if (item?.route) {
    router.push(item.route);
    return;
  }
  activeMenu.value = item.key;
};

onMounted(() => {
  if (userStore.token && !userStore.userInfo?.username) {
    userStore.getUser();
  }
});
</script>

<style lang="scss" scoped>
.my-center {
  min-height: 100vh;
  padding: 86px 240px 96px;
  font-family: var(--ios-font, inherit);
}

.my-center__inner {
  display: flex;
  align-items: flex-start;
  gap: 56px;
  padding-top: 36px;
}

.mc-sidebar {
  flex-shrink: 0;
  width: 380px;
  position: sticky;
  top: calc(86px + 36px);
}

.mc-sidebar__panel {
  padding: 36px 28px 32px;
  border-radius: var(--ios-radius-xl, 24px);
  border: 0.5px solid var(--ux-glass-border);
  background: var(--ux-glass-bg);
  backdrop-filter: var(--ios-glass-blur);
  -webkit-backdrop-filter: var(--ios-glass-blur);
  box-shadow: var(--ux-glass-shadow);
}

.mc-sidebar__profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-bottom: 36px;
  margin-bottom: 12px;
  border-bottom: 0.5px solid var(--ux-glass-border);
}

.mc-sidebar__name {
  margin-top: 20px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ux-text-primary);
  line-height: 1.25;
  word-break: break-word;
}

.mc-sidebar__meta {
  margin-top: 8px;
  font-size: 16px;
  color: var(--ux-text-secondary, #9fb4d6);
  line-height: 1.4;
}

.mc-nav__section + .mc-nav__section {
  margin-top: 28px;
}

.mc-nav__section-title {
  padding: 0 8px 12px;
  font-size: 15px;
  font-weight: 600;
  color: var(--ux-text-secondary, #9fb4d6);
  letter-spacing: 0.02em;
}

.mc-nav__group {
  overflow: hidden;
  border-radius: var(--ios-radius-lg, 20px);
  border: 0.5px solid var(--ux-glass-border);
  background: var(--ux-glass-bg-subtle);
}

.mc-nav__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 62px;
  padding: 0 20px;
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 500;
  color: var(--ux-text-primary);
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  &::after {
    content: "";
    position: absolute;
    left: 20px;
    right: 20px;
    bottom: 0;
    height: 0.5px;
    background: var(--ux-glass-border);
    opacity: 0.7;
  }

  &:last-child::after {
    display: none;
  }

  &:hover {
    background: var(--ios-fill-secondary);
  }
}

.mc-nav__item--active {
  background: linear-gradient(
    90deg,
    rgba(0, 212, 255, 0.16) 0%,
    rgba(167, 139, 250, 0.08) 100%
  );

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 12px;
    bottom: 12px;
    width: 4px;
    border-radius: 0 4px 4px 0;
    background: linear-gradient(
      180deg,
      var(--ux-accent, #00d4ff) 0%,
      var(--ux-accent-2, #a78bfa) 100%
    );
    box-shadow: 0 0 14px rgba(0, 212, 255, 0.5);
  }

  .mc-nav__icon {
    color: var(--ux-accent, #00d4ff);
  }

  .mc-nav__label {
    font-weight: 600;
    font-size: 18px;
  }
}

.mc-nav__icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ux-text-secondary, #9fb4d6);
  background: var(--ux-glass-bg-subtle);

  :deep(svg) {
    width: 22px;
    height: 22px;
  }
}

.mc-nav__item--active .mc-nav__icon {
  background: rgba(0, 212, 255, 0.12);
}

.mc-nav__label {
  flex: 1;
  min-width: 0;
}

.mc-nav__trail {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ux-text-muted, rgba(159, 180, 214, 0.5));

  svg {
    width: 18px;
    height: 18px;
  }
}

.mc-main {
  flex: 1;
  min-width: 0;
}

.mc-main__head {
  margin-bottom: 28px;
}

.mc-main__title {
  margin: 0;
  font-size: clamp(24px, 2.2vw, 30px);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.03em;
}

.mc-main__subtitle {
  margin: 10px 0 0;
  font-size: 15px;
  line-height: 1.45;
  color: var(--ux-text-secondary, #9fb4d6);
}

.mc-main__body {
  width: 100%;
  max-width: 720px;
}

@media (max-width: 1200px) {
  .my-center {
    padding-left: 48px;
    padding-right: 48px;
  }

  .my-center__inner {
    gap: 36px;
  }

  .mc-sidebar {
    width: 340px;
  }
}

@media (max-width: 900px) {
  .my-center {
    padding: 86px 20px 64px;
  }

  .my-center__inner {
    flex-direction: column;
    gap: 24px;
    padding-top: 24px;
  }

  .mc-sidebar {
    position: static;
    width: 100%;
  }

  .mc-nav__group {
    display: flex;
    flex-direction: row;
    border-radius: var(--ios-radius-md, 14px);
  }

  .mc-nav__item {
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
    min-height: 72px;
    padding: 10px 8px;
    text-align: center;

    &::after,
    &::before {
      display: none !important;
    }
  }

  .mc-nav__item--active {
    background: rgba(0, 212, 255, 0.12);
    box-shadow: inset 0 -2px 0 var(--ux-accent, #00d4ff);
  }

  .mc-nav__trail {
    display: none;
  }

  .mc-main__body {
    max-width: none;
  }
}
</style>
